import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { parseDobString, buildDobString } from '../../utils/dobDateUtils';

/**
 * isLeapYear
 * ----------
 * Determines if a given year is a leap year using the standard Gregorian calendar rules:
 * - Divisible by 4, AND
 * - Either NOT divisible by 100, OR divisible by 400
 * 
 * Examples:
 * - 2024 → true (divisible by 4, not by 100)
 * - 2000 → true (divisible by 400)
 * - 1900 → false (divisible by 100 but not 400)
 * - 2023 → false (not divisible by 4)
 * 
 * @param {number} year - The year to check
 * @returns {boolean} True if the year is a leap year
 */
const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

/**
 * getDaysInMonth
 * --------------
 * Returns the number of days in a given month, accounting for leap years.
 * 
 * For February:
 * - If year is provided and is a leap year → 29 days
 * - If year is provided and is NOT a leap year → 28 days
 * - If year is NOT provided → 29 days (optimistic, allows user to select 29 and validate later)
 * 
 * @param {number} monthNum - Month number (1-12)
 * @param {number|null} yearNum - Year (optional, used for Feb leap year check)
 * @returns {number} Number of days in the month (28-31)
 */
const getDaysInMonth = (monthNum, yearNum = null) => {
  // Months with 31 days: Jan(1), Mar(3), May(5), Jul(7), Aug(8), Oct(10), Dec(12)
  // Months with 30 days: Apr(4), Jun(6), Sep(9), Nov(11)
  // February: 28 or 29 depending on leap year
  const daysPerMonth = {
    1: 31,  // January
    2: 29,  // February (default to 29; we'll reduce to 28 if year is known and not a leap year)
    3: 31,  // March
    4: 30,  // April
    5: 31,  // May
    6: 30,  // June
    7: 31,  // July
    8: 31,  // August
    9: 30,  // September
    10: 31, // October
    11: 30, // November
    12: 31  // December
  };

  let days = daysPerMonth[monthNum] || 31;

  // For February: only reduce to 28 if we KNOW the year and it's NOT a leap year.
  // If no year is provided, we optimistically allow 29 days.
  if (monthNum === 2 && yearNum && !isLeapYear(yearNum)) {
    days = 28;
  }

  return days;
};

/**
 * DateOfBirthPicker (RMS themed)
 * ------------------------------
 * What:
 *   A lightweight day/month/year picker that emits an ISO date string (YYYY-MM-DD)
 *   when a valid date is fully selected. This is a copy of the ADHD product’s
 *   component, rethemed to use the RMS brand `primary` color instead of violet.
 *
 * Why:
 *   - Keeps the RMS visual language consistent (focus rings, hover states).
 *   - Avoids cross-product styling conflicts by colocating a themed copy under
 *     `src/rms/components`.
 *
 * Features:
 *   - Smart day validation: The day dropdown only shows valid days for the selected
 *     month/year combination (e.g., Feb shows 28 or 29 days based on leap year).
 *   - Auto-clear invalid days: If the user selects a month where their current day
 *     is invalid (e.g., 31st November), the day is automatically cleared.
 *   - Leap year handling: February correctly shows 29 days for leap years when
 *     the year is selected.
 *
 * Props:
 *  - value?: string (ISO 'YYYY-MM-DD')
 *  - onChange: (value: string) => void
 *  - placeholder?: string
 *  - className?: string
 *  - minAgeYears?: number  // youngest allowed age (default 18)
 *  - maxAgeYears?: number  // oldest allowed age (default 120)
 */
export default function DateOfBirthPicker({ value, onChange, placeholder, className, minAgeYears = 18, maxAgeYears = 120 }) {
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  // Initialize internal state whenever an external value arrives/changes.
  // Uses parseDobString (pure string splitting) instead of the Date
  // constructor to avoid UTC/local-time mismatch.
  useEffect(() => {
    if (value) {
      const parts = parseDobString(value);
      if (parts) {
        setDay(parts.day);
        setMonth(parts.month);
        setYear(parts.year);
        return;
      }
    }
    setDay('');
    setMonth('');
    setYear('');
  }, [value]);

  // Calculate the maximum valid days for the currently selected month/year.
  // This accounts for varying month lengths and leap years for February.
  const maxDaysInSelectedMonth = useMemo(() => {
    if (!month) return 31; // No month selected yet, show all days
    const monthNum = parseInt(month, 10);
    const yearNum = year ? parseInt(year, 10) : null;
    return getDaysInMonth(monthNum, yearNum);
  }, [month, year]);

  // Dynamic day options - only show days valid for the selected month/year.
  // For example, November will only show days 1-30, and February will show
  // 1-28 or 1-29 depending on whether the selected year is a leap year.
  const days = useMemo(() => {
    return Array.from({ length: maxDaysInSelectedMonth }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      label: String(i + 1)
    }));
  }, [maxDaysInSelectedMonth]);

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Compute allowed year range by age bounds.
  // We add 1 to maxAgeYears when calculating startYear because someone who is
  // X years old today could have been born in (currentYear - X - 1) if their
  // birthday is later in the year.
  // Example: In Jan 2026, a 17-year-old could have been born in:
  //   - 2008 (if birthday is after today) or
  //   - 2009 (if birthday is before/on today)
  // So for maxAgeYears=17, we need to include 2008, not just 2009.
  const currentYear = new Date().getFullYear();
  const endYear = currentYear - Number(minAgeYears || 0); // youngest allowed birth year
  const startYear = currentYear - Number(maxAgeYears || 120) - 1; // oldest allowed birth year (with buffer)
  const years = Array.from({ length: Math.max(0, endYear - startYear + 1) }, (_, i) => {
    const y = endYear - i;
    return { value: String(y), label: String(y) };
  });

  /**
   * updateDate
   * ----------
   * Attempts to construct and emit a valid date from the selected day/month/year.
   * Only emits if the date is valid (handles edge cases like 31 Feb automatically).
   * Uses buildDobString (UTC-safe validation) instead of new Date(isoString) +
   * local-time accessors to avoid timezone mismatch.
   */
  const updateDate = (d, m, y) => {
    if (d && m && y) {
      const dateString = buildDobString(d, m, y);
      if (dateString) {
        onChange?.(dateString);
      }
    } else if (!d && !m && !y) {
      onChange?.('');
    }
  };

  /**
   * isDayValidForMonth
   * ------------------
   * Checks if a given day value is valid for a given month/year combination.
   * Used to determine if we need to clear the day when month/year changes.
   * 
   * @param {string} dayVal - Day value (01-31)
   * @param {string} monthVal - Month value (01-12)
   * @param {string|null} yearVal - Year value (optional, for Feb leap year check)
   * @returns {boolean} True if the day is valid for the month/year
   */
  const isDayValidForMonth = (dayVal, monthVal, yearVal = null) => {
    if (!dayVal || !monthVal) return true; // If either is missing, nothing to validate
    const dayNum = parseInt(dayVal, 10);
    const monthNum = parseInt(monthVal, 10);
    const yearNum = yearVal ? parseInt(yearVal, 10) : null;
    const maxDays = getDaysInMonth(monthNum, yearNum);
    return dayNum <= maxDays;
  };

  const handleDayChange = (newDay) => {
    setDay(newDay);
    updateDate(newDay, month, year);
  };

  /**
   * handleMonthChange
   * -----------------
   * When the month changes, we need to check if the currently selected day
   * is still valid for the new month. For example:
   * - User has 31st selected, then picks November (30 days) → clear day
   * - User has 30th or 31st selected, then picks February → clear day
   * 
   * Special case for February 29th:
   * - If user has 29th selected and picks February, we DON'T clear immediately
   * - We wait until a year is selected to know if it's a leap year
   * - This provides a better UX - don't assume invalid until we know for sure
   */
  const handleMonthChange = (newMonth) => {
    setMonth(newMonth);
    
    // Special handling for February with day 29:
    // If no year is selected yet, we can't know if 29 is valid (depends on leap year).
    // So we defer validation until year is selected, giving the user benefit of the doubt.
    const isFebruary = newMonth === '02';
    const dayNum = day ? parseInt(day, 10) : 0;
    const isFeb29Pending = isFebruary && dayNum === 29 && !year;
    
    if (isFeb29Pending) {
      // Don't clear yet - wait for year to be selected to validate
      // Don't call updateDate since we don't have a complete date
      return;
    }
    
    // For all other cases, check if day is valid for the new month
    // This handles: 31st on 30-day months, 30th/31st on February, etc.
    if (day && !isDayValidForMonth(day, newMonth, year)) {
      // Day is definitively invalid for this month - clear it
      setDay('');
      // Don't call updateDate since we don't have a complete valid date
      return;
    }
    
    updateDate(day, newMonth, year);
  };

  /**
   * handleYearChange
   * ----------------
   * When the year changes, we need to check if the currently selected day
   * is still valid. This mainly affects February 29th - if the user switches
   * from a leap year to a non-leap year while having Feb 29 selected, we
   * need to clear the day.
   */
  const handleYearChange = (newYear) => {
    setYear(newYear);
    
    // Check if the current day is valid for the existing month + new year
    // This catches the Feb 29 → non-leap year edge case
    if (day && month && !isDayValidForMonth(day, month, newYear)) {
      // Day is no longer valid (e.g., Feb 29 on non-leap year) - clear it
      setDay('');
      // Don't call updateDate since we don't have a complete valid date
      return;
    }
    
    updateDate(day, month, newYear);
  };

  // Themed select using `primary` color for focus/hover states.
  // The dropdown is positioned intelligently to stay within viewport bounds:
  // - If there's enough space below, it opens downward
  // - If not, it opens upward (above the button)
  // - Max height is constrained to available space with scrolling enabled
  const Select = ({ value: v, onChange: onSelChange, options, placeholder: ph, className: cn }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const anchorRef = React.useRef(null);
    const selected = options.find(opt => opt.value === v);

    // Minimum padding from viewport edges (in pixels)
    const VIEWPORT_PADDING = 16;
    // Minimum height for the dropdown to be usable
    const MIN_DROPDOWN_HEIGHT = 150;

    const openMenu = () => {
      try {
        const rect = anchorRef.current?.getBoundingClientRect();
        if (rect) {
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
          const spaceAbove = rect.top - VIEWPORT_PADDING;
          
          // Decide whether to open above or below based on available space
          // Prefer below unless there's significantly more space above
          const openAbove = spaceBelow < MIN_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;
          
          // Calculate max height based on available space (capped at 40vh for aesthetics)
          const maxAvailableHeight = openAbove ? spaceAbove : spaceBelow;
          const maxHeight = Math.min(maxAvailableHeight, viewportHeight * 0.4);
          
          if (openAbove) {
            // Position above the button, with bottom aligned to button's top
            setMenuPos({
              left: rect.left,
              bottom: viewportHeight - rect.top + 4,
              width: rect.width,
              maxHeight,
              openAbove: true
            });
          } else {
            // Position below the button (default behavior)
            setMenuPos({
              left: rect.left,
              top: rect.bottom + 4,
              width: rect.width,
              maxHeight,
              openAbove: false
            });
          }
        }
      } catch (_) {}
      setOpen(true);
    };

    return (
      <div className={`relative ${cn}`} ref={anchorRef}>
        <button
          type="button"
          className="w-full p-2 border-2 border-gray-300 rounded-lg bg-white text-black text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          onClick={() => (open ? setOpen(false) : openMenu())}
        >
          <span className={selected ? 'text-black' : 'text-gray-500'}>
            {selected ? selected.label : ph}
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open && (
          <>
            {/* Full-screen backdrop to capture clicks/scrolls; high z-index to sit above cards */}
            <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
            {/* Fixed-position dropdown with smart positioning to stay within viewport */}
            <div
              className="fixed z-[80] bg-white border border-gray-300 rounded-md shadow-lg overflow-auto"
              style={{
                left: menuPos?.left || 0,
                // Use top or bottom positioning based on where dropdown opens
                ...(menuPos?.openAbove
                  ? { bottom: menuPos?.bottom || 0 }
                  : { top: menuPos?.top || 0 }),
                width: menuPos?.width || 'auto',
                maxHeight: menuPos?.maxHeight || '60vh'
              }}
            >
              {options.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-primary/5 focus:bg-primary/5 focus:outline-none text-black"
                  onClick={() => { onSelChange(opt.value); setOpen(false); }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-2 ${className || ''}`}>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Day</label>
          <Select value={day} onChange={handleDayChange} options={days} placeholder="Day" className="w-full" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
          <Select value={month} onChange={handleMonthChange} options={months} placeholder="Month" className="w-full" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
          <Select value={year} onChange={handleYearChange} options={years} placeholder="Year" className="w-full" />
        </div>
      </div>
    </div>
  );
}


